import { StyleSheet, css } from 'aphrodite';
import { type FC, useEffect, useState } from 'react';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { Input } from '../../../web/components-v2/Input';
import type { FocusedLead } from '../data';

/**
 * Edit Company Details dialog.
 *
 * Scope for v1: editing the two fields the rep cares about most — company
 * name and website. The legacy `CallingCard.renderEditingLeadInfo` modal
 * also includes a phone-numbers repeater; that's a follow-up because the
 * underlying component is tangled with MobX. Until then, phone editing
 * happens in the legacy view.
 *
 * `lead` is read for the initial values but the actual save is owned by
 * `FocusedLeadView` (which holds the mutation hook) — this dialog is
 * presentation-only.
 */
export type EditCompanyDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	lead: FocusedLead | undefined;
	onSave: (updates: { companyName: string; webSite: string }) => void;
	saving?: boolean;
};

export const EditCompanyDialog: FC<EditCompanyDialogProps> = ({ open, onOpenChange, lead, onSave, saving }) => {
	const [companyName, setCompanyName] = useState('');
	const [webSite, setWebSite] = useState('');

	// Reset form values whenever the dialog opens for a (possibly new) lead.
	useEffect(() => {
		if (!open) return;
		setCompanyName(lead?.company ?? '');
		setWebSite(lead?.website ?? '');
	}, [open, lead?.company, lead?.website]);

	const canSave = companyName.trim().length > 0 && !saving;

	const handleSave = () => {
		if (!canSave) return;
		onSave({ companyName: companyName.trim(), webSite: webSite.trim() });
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Edit company details</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<div className={css(styles.field)}>
						<label htmlFor='focused-edit-company-name' className={css(styles.label)}>
							Company name
						</label>
						<Input
							id='focused-edit-company-name'
							value={companyName}
							onChange={e => setCompanyName(e.target.value)}
							autoFocus
							disabled={saving}
						/>
					</div>
					<div className={css(styles.field)}>
						<label htmlFor='focused-edit-website' className={css(styles.label)}>
							Website
						</label>
						<Input
							id='focused-edit-website'
							value={webSite}
							onChange={e => setWebSite(e.target.value)}
							placeholder='example.com'
							disabled={saving}
						/>
					</div>
					<p className={css(styles.helper)}>Phone numbers can be edited from the legacy lead view for now.</p>
				</DialogBody>
				<DialogFooter>
					<Button variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
						Cancel
					</Button>
					<Button variant='primary' onClick={handleSave} disabled={!canSave}>
						{saving ? 'Saving…' : 'Save'}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

const styles = StyleSheet.create({
	field: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		marginBottom: 14,
	},
	label: {
		fontSize: 12,
		fontWeight: 500,
		color: 'var(--ink-muted)',
	},
	helper: {
		marginTop: 4,
		fontSize: 11,
		color: 'var(--ink-faint)',
	},
});
